/**
 * LostUsersChart
 *
 * Lost Paying Users chart with daily breakdown and statistics.
 */
import React from 'react';
import { Info, TrendingDown, Activity, Calendar } from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  Tooltip as RechartsTooltip
} from 'recharts';
import AnalyticsTooltip from './AnalyticsTooltip';

const LostUsersChart = ({ analyticsData, formatDate, formatDateLong, getPeriodDisplayName, selectedPeriod }) => {
  const lostData = analyticsData.lostUsersTrends || [];
  const summary = analyticsData.summary || {};
  
  // Calculate totals
  const total = lostData.reduce((sum, day) => sum + day.lostUsers, 0);
  const dailyAvg = lostData.length > 0 ? total / lostData.length : 0;
  
  // Get lifetime total
  const lifetimeTotal = (() => {
    if (selectedPeriod === 'all' && typeof summary.lostPayingUsersLifetime === 'number') {
      return summary.lostPayingUsersLifetime;
    }
    if (typeof summary.periodLostPayingUsers === 'number') return summary.periodLostPayingUsers;
    return total;
  })();

  // Get all-time totals
  const lifetimeDeleted = (typeof summary.deletedPayingUsersLifetime === 'number') 
    ? summary.deletedPayingUsersLifetime 
    : (typeof summary.deletedPayingCustomers === 'number' ? summary.deletedPayingCustomers : 0);
  const lifetimeFormer = (typeof summary.formerSubscribersLifetime === 'number') 
    ? summary.formerSubscribersLifetime 
    : (typeof summary.churnedUsers === 'number' ? summary.churnedUsers : 0);
  const allTimeTotal = (typeof summary.lostPayingUsersLifetime === 'number') 
    ? summary.lostPayingUsersLifetime 
    : (lifetimeDeleted + lifetimeFormer);

  return (
    <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Lost Paying Users
        </h3>
        <AnalyticsTooltip content="Daily count of paying customers lost through account deletions and subscription cancellations. Helps track churn patterns and identify retention opportunities." id="lost-users-tooltip">
          <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
        </AnalyticsTooltip>
      </div>
      
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={lostData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
          <XAxis 
            dataKey="date" 
            tickFormatter={formatDate}
            stroke="#6B7280"
            fontSize={12}
            interval="preserveStartEnd"
            minTickGap={30}
          />
          <YAxis 
            stroke="#6B7280"
            fontSize={12}
            allowDecimals={false}
          />
          <RechartsTooltip 
            labelFormatter={(value) => formatDateLong(value)}
            formatter={(value) => [value, 'Lost Users']}
            contentStyle={{
              backgroundColor: '#1F2937',
              border: '1px solid #374151',
              borderRadius: '8px',
              color: '#F9FAFB'
            }}
          />
          <Bar 
            dataKey="lostUsers" 
            fill="#EF4444"
            radius={[4, 4, 0, 0]}
            name="Lost Users"
          />
        </BarChart>
      </ResponsiveContainer>
      
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Lost This Period */}
        <div className="bg-red-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
              <TrendingDown className="h-4 w-4 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-red-700 dark:text-red-300 font-medium">Total {getPeriodDisplayName(selectedPeriod)}</p>
              <p className="text-lg font-bold text-red-900 dark:text-red-100">
                {lifetimeTotal} users
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">Lost customers</p>
            </div>
          </div>
        </div>

        {/* Daily Average */}
        <div className="bg-orange-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/40 rounded-lg">
              <Activity className="h-4 w-4 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-sm text-orange-700 dark:text-orange-300 font-medium">Daily Average</p>
              <p className="text-lg font-bold text-orange-900 dark:text-orange-100">
                {dailyAvg.toFixed(1)} users
              </p>
              <p className="text-xs text-orange-600 dark:text-orange-400">Per day</p>
            </div>
          </div>
        </div>

        {/* Weekly Total */}
        <div className="bg-yellow-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg">
              <Calendar className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 font-medium">Weekly Rate</p>
              <p className="text-lg font-bold text-yellow-900 dark:text-yellow-100">
                {(dailyAvg * 7).toFixed(1)} users
              </p>
              <p className="text-xs text-yellow-600 dark:text-yellow-400">Per week</p>
            </div>
          </div>
        </div>

        {/* Monthly Rate */}
        <div className="bg-purple-50 dark:bg-transparent backdrop-blur-md rounded-lg p-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
              <TrendingDown className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-purple-700 dark:text-purple-300 font-medium">Monthly Rate</p>
              <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                {(dailyAvg * 30).toFixed(1)} users
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400">Per month</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total All Time</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {allTimeTotal} users
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Deleted paid + former subscribers</p>
        </div>
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Deleted Paying (All Time)</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {lifetimeDeleted} users
          </p>
        </div>
        <div className="bg-white dark:bg-transparent backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Former Subscribers (All Time)</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {lifetimeFormer} users
          </p>
        </div>
      </div>

      <div className="mt-4 p-4 bg-gray-50 dark:bg-transparent backdrop-blur-md rounded-lg">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          <strong>💡 Retention insight:</strong> Monitor patterns in lost users to identify potential issues. 
          High churn on specific days might indicate billing problems, product issues, or competitive actions. 
          Use this data to proactively reach out to at-risk customers and improve retention strategies.
        </p>
      </div>
    </div>
  );
};

export default LostUsersChart;


